import { Clock, MapPin, MessageCircle, Phone, Share2 } from 'lucide-react';

import { CONTACT_DETAILS, mapsDirectionsHref, whatsappHref } from '../contact-details';

import { StaggerGroup, StaggerItem } from '@/components/motion/reveal';
import { type SocialPlatform, SocialIcon } from '@/components/social-icon';

/** Premium, perfectly aligned contact info section: address, contact methods, hours, and socials. */
export function ContactInfo() {
  return (
    <StaggerGroup className="flex flex-col gap-5">
      {/* 1. Visit Our Office */}
      <InfoCard icon={MapPin} title="Visit our office">
        <address className="text-muted-foreground not-italic text-sm leading-relaxed">
          {CONTACT_DETAILS.addressLines.map((line) => (
            <span key={line} className="block">
              {line}
            </span>
          ))}
        </address>
        <div className="mt-3 flex items-center justify-between border-t border-border/50 pt-3">
          <span className="text-xs text-muted-foreground">Visit us in person</span>
          <a
            href={mapsDirectionsHref()}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-primary/80 duration-fast ease-out-expo inline-flex items-center gap-1 text-sm font-semibold transition-colors hover:underline"
          >
            Get directions →
          </a>
        </div>
      </InfoCard>

      {/* 2. Get In Touch (Phone, Email & WhatsApp) */}
      <InfoCard icon={Phone} title="Get in touch">
        <div className="flex flex-col gap-3.5">
          {/* Phone */}
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Phone</p>
              <a
                href={CONTACT_DETAILS.phones[0]?.href}
                className="text-foreground hover:text-primary text-sm font-medium transition-colors"
              >
                {CONTACT_DETAILS.phones[0]?.value}
              </a>
            </div>
            <span className="text-xs text-muted-foreground">Mon–Sat, 9am–7pm</span>
          </div>

          <div className="border-t border-border/50" />

          {/* Email */}
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Email</p>
              <a
                href={`mailto:${CONTACT_DETAILS.email}`}
                className="text-foreground hover:text-primary text-sm font-medium transition-colors"
              >
                {CONTACT_DETAILS.email}
              </a>
            </div>
            <span className="text-xs text-muted-foreground">Replies in 24h</span>
          </div>

          <div className="border-t border-border/50" />

          {/* WhatsApp */}
          <div className="flex items-center justify-between gap-3 pt-0.5">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">WhatsApp</p>
              <p className="text-xs text-muted-foreground">Instant advisor chat</p>
            </div>
            <a
              href={whatsappHref()}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#25D366] text-white hover:bg-[#20bd5a] duration-fast ease-out-expo inline-flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-xs font-semibold shadow-sm transition-all hover:-translate-y-0.5"
            >
              <MessageCircle className="size-3.5" aria-hidden="true" />
              Chat Now
            </a>
          </div>
        </div>
      </InfoCard>

      {/* 3. Business Hours */}
      <InfoCard icon={Clock} title="Business hours">
        <ul className="flex flex-col gap-2.5">
          {CONTACT_DETAILS.hours.map((h) => (
            <li key={h.day} className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground font-medium">{h.day}</span>
              <span className="text-foreground font-semibold whitespace-nowrap">{h.time}</span>
            </li>
          ))}
        </ul>
      </InfoCard>

      {/* 4. Follow Us / Socials */}
      <InfoCard icon={Share2} title="Follow us">
        <ul className="flex flex-wrap gap-2.5">
          {CONTACT_DETAILS.social.map((s) => (
            <li key={s.label}>
              <a
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="border-border bg-background text-foreground hover:border-primary/50 hover:text-primary duration-fast ease-out-expo inline-flex items-center gap-2 rounded-lg border px-3.5 py-2 text-xs font-medium transition-all hover:-translate-y-0.5 shadow-xs"
              >
                <SocialIcon platform={s.label as SocialPlatform} className="size-4" />
                {s.label}
              </a>
            </li>
          ))}
        </ul>
      </InfoCard>
    </StaggerGroup>
  );
}

function InfoCard({
  icon: Icon,
  title,
  children,
  className,
}: {
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <StaggerItem
      className={
        'border-border bg-card flex flex-col gap-4 rounded-xl border p-5 sm:p-6 shadow-md transition-all duration-base ease-out-expo hover:shadow-lg ' +
        (className ?? '')
      }
    >
      <div className="flex items-center gap-3">
        <span className="bg-primary/10 text-primary flex size-10 shrink-0 items-center justify-center rounded-lg">
          <Icon className="size-5" aria-hidden />
        </span>
        <h2 className="text-foreground font-display text-lg font-semibold tracking-tight">{title}</h2>
      </div>
      <div>{children}</div>
    </StaggerItem>
  );
}
